import Joi from 'joi';
import {useCallback, useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import {businessAffiliateService} from '@/services/businessAffiliateService';
import type {
  AdminBusinessAffiliateTier,
  CreateAffiliateTierPayload,
  UpdateAffiliateTierPayload,
} from '@/types';
import {BusinessAffiliateRewardModel} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';

import type {AffiliateTiersProps, TierFieldErrors} from './types';

// -1 means no cap; anything else must be a real amount.
const noCapOrAmount = Joi.alternatives().try(
  Joi.number().valid(-1),
  Joi.number().min(0),
);

// A payout cap of 0 would hold every accrual forever, so it must be -1 or
// strictly positive.
const noCapOrPositiveAmount = Joi.alternatives().try(
  Joi.number().valid(-1),
  Joi.number().greater(0),
);

const createSchema = Joi.object({
  name: Joi.string().trim().min(1).max(100).required().label('Name'),
  rewardModel: Joi.string()
    .valid(...Object.values(BusinessAffiliateRewardModel))
    .required()
    .label('Reward model'),
  activationWindowDays: Joi.number()
    .integer()
    .min(1)
    .max(365)
    .required()
    .label('Activation window'),
  rewardWindowDays: Joi.number()
    .integer()
    .min(1)
    .max(3650)
    .required()
    .label('Reward window'),
  termsVersion: Joi.string()
    .trim()
    .min(1)
    .max(32)
    .required()
    .label('Terms version'),
  isDefault: Joi.boolean(),
  refereeDiscountPercent: Joi.number()
    .min(0)
    .max(100)
    .label('Referee discount'),
  maxRewardPerReferralUSD: noCapOrAmount.label('Max reward per referral'),
  maxRewardPerPayoutUSD: noCapOrPositiveAmount.label('Max reward per payout'),
  requiresVerifiedReferrer: Joi.boolean(),
  referrerSharePercent: Joi.when('rewardModel', {
    is: BusinessAffiliateRewardModel.FeeShare,
    then: Joi.number().min(0).max(100).required(),
    otherwise: Joi.forbidden(),
  }).label('Referrer share'),
  volumeThresholdUSD: Joi.when('rewardModel', {
    is: BusinessAffiliateRewardModel.FlatAtThreshold,
    then: Joi.number().min(0).required(),
    otherwise: Joi.forbidden(),
  }).label('Volume threshold'),
  flatRewardUSD: Joi.when('rewardModel', {
    is: BusinessAffiliateRewardModel.FlatAtThreshold,
    then: Joi.number().min(0).required(),
    otherwise: Joi.forbidden(),
  }).label('Flat reward'),
  volumeRewardBasisPoints: Joi.when('rewardModel', {
    is: BusinessAffiliateRewardModel.VolumePercentage,
    then: Joi.number().min(0).max(10_000).required(),
    otherwise: Joi.forbidden(),
  }).label('Volume reward'),
});

const updateSchema = Joi.object({
  name: Joi.string().trim().min(1).max(100).label('Name'),
  isDeactivated: Joi.boolean(),
  activationWindowDays: Joi.number()
    .integer()
    .min(1)
    .max(365)
    .label('Activation window'),
  rewardWindowDays: Joi.number()
    .integer()
    .min(1)
    .max(3650)
    .label('Reward window'),
  refereeDiscountPercent: Joi.number()
    .min(0)
    .max(100)
    .label('Referee discount'),
  maxRewardPerReferralUSD: noCapOrAmount.label('Max reward per referral'),
  maxRewardPerPayoutUSD: noCapOrPositiveAmount.label('Max reward per payout'),
  requiresVerifiedReferrer: Joi.boolean(),
  termsVersion: Joi.string().trim().min(1).max(32).label('Terms version'),
}).min(1);

const toFieldErrors = (error: Joi.ValidationError): TierFieldErrors => {
  const fieldErrors: TierFieldErrors = {};
  error.details.forEach((detail) => {
    const key = detail.path[0] as keyof CreateAffiliateTierPayload;
    fieldErrors[key] = detail.message;
  });
  return fieldErrors;
};

const useAffiliateTiers = (): AffiliateTiersProps => {
  const [tiers, setTiers] = useState<AdminBusinessAffiliateTier[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [errors, setErrors] = useState<TierFieldErrors>({});

  const applyTiers = useCallback((data: AdminBusinessAffiliateTier[]) => {
    setTiers(data);
    setError(null);
    setIsLoading(false);
  }, []);

  const failLoad = useCallback((e: unknown) => {
    setError(e);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    businessAffiliateService.getTiers().then(applyTiers).catch(failLoad);
  }, [applyTiers, failLoad]);

  const fetchTiers = useCallback(async () => {
    setIsLoading(true);
    try {
      applyTiers(await businessAffiliateService.getTiers());
    } catch (e) {
      failLoad(e);
    }
  }, [applyTiers, failLoad]);

  const validateCreate = useCallback(
    (form: CreateAffiliateTierPayload): boolean => {
      const result = createSchema.validate(form, {abortEarly: false});
      if (result.error) {
        setErrors(toFieldErrors(result.error));
        return false;
      }
      setErrors({});
      return true;
    },
    [],
  );

  const validateUpdate = useCallback(
    (form: UpdateAffiliateTierPayload): boolean => {
      const result = updateSchema.validate(form, {abortEarly: false});
      if (result.error) {
        setErrors(toFieldErrors(result.error));
        return false;
      }
      setErrors({});
      return true;
    },
    [],
  );

  const handleCreate = useCallback(
    async (form: CreateAffiliateTierPayload): Promise<boolean> => {
      if (!validateCreate(form)) {
        return false;
      }
      try {
        setIsSaving(true);
        await businessAffiliateService.createTier(form);
        await fetchTiers();
        toast.success('Affiliate tier created');
        return true;
      } catch (e) {
        toast.error(getErrorMessage(e, 'Could not create the affiliate tier'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [fetchTiers, validateCreate],
  );

  const handleUpdate = useCallback(
    async (
      tierId: string,
      form: UpdateAffiliateTierPayload,
    ): Promise<boolean> => {
      if (!validateUpdate(form)) {
        return false;
      }
      try {
        setIsSaving(true);
        await businessAffiliateService.updateTier(tierId, form);
        await fetchTiers();
        toast.success('Affiliate tier updated');
        return true;
      } catch (e) {
        toast.error(getErrorMessage(e, 'Could not update the affiliate tier'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [fetchTiers, validateUpdate],
  );

  const makeDefault = useCallback(
    async (tierId: string): Promise<boolean> => {
      try {
        setIsSaving(true);
        await businessAffiliateService.setDefaultTier(tierId);
        await fetchTiers();
        toast.success('New referrals will be captured under this tier');
        return true;
      } catch (e) {
        toast.error(getErrorMessage(e, 'Could not set the default tier'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [fetchTiers],
  );

  const clearErrors = useCallback(() => setErrors({}), []);

  return {
    tiers,
    isLoading,
    isSaving,
    errors,
    error,
    validateCreate,
    validateUpdate,
    handleCreate,
    handleUpdate,
    makeDefault,
    clearErrors,
  };
};

export default useAffiliateTiers;
